import { useState } from 'react'

import { useProcedureListTableLogic } from 'components/ProcedureList/ProcedureListTable.logic'
import { useGetSurgeriesQuery } from 'lib/apollo/hooks'
import { useDisablePolling } from 'lib/useDisablePolling'
import dayjs from 'lib/dayjs'

import { ItemResponse } from 'common/types'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'
import { useApolloClients } from 'lib/apollo/apolloProvider'
import { useEnterpriseStore } from 'store'

const initialBefore = dayjs().endOf('day').toDate()
const initialAfter = dayjs().startOf('day').toDate()

export const useListOfSurgeries = ({
  traySurgeryId,
}: {
  traySurgeryId: string
}) => {
  const disablePolling = useDisablePolling()
  const [beforeDate, setBeforeDate] = useState(initialBefore)
  const [afterDate, setAfterDate] = useState(initialAfter)

  const { servicesClient: client } = useApolloClients()

  const { selectedDepartment, isDepartmentsLoaded } = useEnterpriseStore()

  const {
    loading: apolloLoading,
    error,
    data,
    refetch,
  } = useGetSurgeriesQuery(
    {
      before: beforeDate,
      after: afterDate,
      limit: 0,
      departmentId: selectedDepartment?.id,
    },
    {
      client,
      disablePolling,
      skip: !isDepartmentsLoaded,
    }
  )

  const loading = !isDepartmentsLoaded || apolloLoading
  const surgeries = data && data.getSurgeries?.results

  const { isNurse, sortedSurgeries } = useProcedureListTableLogic({
    events: surgeries,
  })

  const handleBackDay = () => {
    setBeforeDate(dayjs(beforeDate).subtract(1, 'day').endOf('day').toDate())
    setAfterDate(dayjs(afterDate).subtract(1, 'day').startOf('day').toDate())
  }

  const handleForwardDay = () => {
    setBeforeDate(dayjs(beforeDate).add(1, 'day').endOf('day').toDate())
    setAfterDate(dayjs(afterDate).add(1, 'day').startOf('day').toDate())
  }

  const isSurgerySelected = (surgeryId: string, trayData: ItemResponse) => {
    const previousProductDetails = extractTrayJSONData(trayData)

    const selectedSurgeryId = traySurgeryId || previousProductDetails?.surgeryId

    return surgeryId === selectedSurgeryId
  }

  return {
    loading,
    sortedSurgeries,
    isNurse,
    handleBackDay,
    handleForwardDay,
    isSurgerySelected,
    beforeDate,
    afterDate,
    error,
    refetch,
  }
}
